import React, { createContext, useContext, useState, useEffect } from "react";
import { API_URL } from "utils/constants";

const SchedulePatternContext = createContext();

export const useSchedulePattern = () => {
  const context = useContext(SchedulePatternContext);
  if (!context) {
    throw new Error("useSchedulePattern must be used within a SchedulePatternProvider");
  }
  return context;
};

export const SchedulePatternProvider = ({ children }) => {
  const [schedulePatterns, setSchedulePatterns] = useState({
    data: [],
    loading: false,
    error: null,
    pagination: {
      current_page: 1,
      last_page: 1,
      per_page: 20,
      total: 0,
    },
  });

  const [filterOptions, setFilterOptions] = useState({
    search: "",
    page: 1,
    per_page: 20,
  });

  // Fetch schedule patterns
  const fetchSchedulePatterns = async (options = {}) => {
    const params = {
      ...filterOptions,
      ...options,
    };

    setSchedulePatterns(prev => ({ ...prev, loading: true, error: null }));

    try {
      const queryString = new URLSearchParams(params).toString();
      const token = localStorage.getItem("token");

      const response = await fetch(`${API_URL}/ahr/schedule-patterns?${queryString}&include=details,details.shift`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch schedule patterns");
      }

      const result = await response.json();

      setSchedulePatterns({
        data: result.data || [],
        loading: false,
        error: null,
        pagination: result.pagination || {
          current_page: 1,
          last_page: 1,
          per_page: 20,
          total: 0,
        },
      });
    } catch (error) {
      setSchedulePatterns(prev => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
    }
  };

  // Handle search
  const handleSearch = (searchTerm) => {
    setFilterOptions(prev => ({
      ...prev,
      search: searchTerm,
      page: 1, // Reset to first page on search
    }));
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setFilterOptions(prev => ({
      ...prev,
      page,
    }));
  };

  // Refetch data
  const refetch = () => {
    fetchSchedulePatterns();
  };

  // Initial fetch
  useEffect(() => {
    fetchSchedulePatterns();
  }, [filterOptions]);

  // Pagination component
  const PaginationComponent = () => {
    if (schedulePatterns.pagination.last_page <= 1) return null;

    const { current_page, last_page } = schedulePatterns.pagination;

    return (
      <div className="d-flex justify-content-center mt-3">
        <nav>
          <ul className="pagination">
            <li className={`page-item ${current_page === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(current_page - 1)}
                disabled={current_page === 1}
              >
                Previous
              </button>
            </li>

            {Array.from({ length: last_page }, (_, i) => i + 1).map(page => (
              <li key={page} className={`page-item ${page === current_page ? 'active' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              </li>
            ))}

            <li className={`page-item ${current_page === last_page ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(current_page + 1)}
                disabled={current_page === last_page}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>
    );
  };

  const value = {
    schedulePatterns,
    filterOptions,
    setFilterOptions,
    handleSearch,
    refetch,
    PaginationComponent,
  };

  return (
    <SchedulePatternContext.Provider value={value}>
      {children}
    </SchedulePatternContext.Provider>
  );
};